import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { useAdminAuth } from '../utils/adminAuth';
import { useCapability } from '../utils/capabilityContext';
import { useTranslation } from 'react-i18next';
import { getActiveWorkspace } from '../utils/workspace';
import { ChartNoAxesCombined, Video } from 'lucide-react';
import logo from '../assets/logo.png';

const Sidebar = ({ isCollapsed, onToggleCollapse }) => {
  const { currentUser } = useAuth();
  const { isAdminAuthenticated } = useAdminAuth();
  const { debugEnabled } = useCapability();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const activeWorkspace = getActiveWorkspace(location.pathname);

  // SVG图标组件
  const iconComponents = {
    realtimeConsultations: () => <Video size={18} strokeWidth={2} />,
    dashboard: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9,22 9,12 15,12 15,22"/>
      </svg>
    ),
    reports: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14,2 14,8 20,8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10,9 9,9 8,9"/>
      </svg>
    ),
    reviewedReports: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14,2 14,8 20,8"/>
        <polyline points="9,15 11,17 15,13"/>
      </svg>
    ),
    appointmentOrders: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
        <path d="M8 14h3"/>
        <path d="M8 18h8"/>
      </svg>
    ),
    patients: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    debugDemo: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v4"/>
        <path d="M12 18v4"/>
        <path d="M4.93 4.93l2.83 2.83"/>
        <path d="M16.24 16.24l2.83 2.83"/>
        <path d="M2 12h4"/>
        <path d="M18 12h4"/>
        <path d="M4.93 19.07l2.83-2.83"/>
        <path d="M16.24 7.76l2.83-2.83"/>
      </svg>
    ),
    doctors: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
        <path d="M12 2v4"/>
        <path d="M12 10v4"/>
        <path d="M8 6h8"/>
      </svg>
    ),
    userCenter: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4"/>
        <path d="M6 21v-2a6 6 0 0 1 12 0v2"/>
        <path d="M16 3.5l1.3 1.3"/>
        <path d="M6.7 4.8L8 3.5"/>
      </svg>
    ),
    clinics: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18"/>
        <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/>
        <path d="M9 8h6"/>
        <path d="M9 12h6"/>
        <path d="M12 16v5"/>
      </svg>
    ),
    healthHouseStations: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="18" height="14" rx="2"/>
        <path d="M8 12h8"/>
        <path d="M12 8v8"/>
        <path d="M7 19v2"/>
        <path d="M17 19v2"/>
      </svg>
    ),
    healthHouseConnectors: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22v-5"/>
        <path d="M9 8V2"/>
        <path d="M15 8V2"/>
        <path d="M18 8v3a6 6 0 0 1-12 0V8z"/>
      </svg>
    ),
    tokenUsage: () => <ChartNoAxesCombined size={18} strokeWidth={2} />,
    collapse: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6"/>
      </svg>
    ),
    expand: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"/>
      </svg>
    )
  };
  const homeItem = activeWorkspace === 'admin'
    ? {
        id: 'admin-home',
        label: t('dashboard.adminHome'),
        icon: 'dashboard',
        path: '/admin'
      }
    : {
        id: 'doctor-home',
        label: t('dashboard.doctorHome'),
        icon: 'dashboard',
        path: '/doctor'
      };
  const menuItems = [homeItem];

  if ((activeWorkspace === 'doctor' || activeWorkspace === 'home') && currentUser) {
    menuItems.push(
      {
        id: 'realtime-consultations',
        label: t('realtimeConsultations.title'),
        icon: 'realtimeConsultations',
        path: '/realtime-consultations'
      },
      {
        id: 'patients',
        label: t('dashboard.patientManagement'),
        icon: 'patients',
        path: '/patients'
      },
      {
        id: 'reports',
        label: t('dashboard.pendingReports'),
        icon: 'reports',
        path: '/reports'
      },
      {
        id: 'reviewed-reports',
        label: t('dashboard.reviewedReports'),
        icon: 'reviewedReports',
        path: '/reviewed-reports'
      },
      {
        id: 'appointment-orders',
        label: t('dashboard.appointmentOrders'),
        icon: 'appointmentOrders',
        path: '/appointment-orders'
      },
      {
        id: 'user-center',
        label: t('dashboard.userCenter'),
        icon: 'userCenter',
        path: '/user-center'
      }
    );
  }

  if ((activeWorkspace === 'admin' || activeWorkspace === 'home') && isAdminAuthenticated) {
    menuItems.push(
      {
        id: 'admin-patients',
        label: t('dashboard.patientManagement'),
        icon: 'patients',
        path: '/admin/patients'
      },
      {
        id: 'token-usage',
        label: t('tokenUsage.menuLabel'),
        icon: 'tokenUsage',
        path: '/admin/token-usage'
      },
      {
        id: 'doctors',
        label: t('dashboard.doctorManagement'),
        icon: 'doctors',
        path: '/doctors'
      },
      {
        id: 'clinics',
        label: t('dashboard.clinicManagement'),
        icon: 'clinics',
        path: '/clinics'
      },
      {
        id: 'health-house-stations',
        label: t('healthHouseStations.menuLabel'),
        icon: 'healthHouseStations',
        path: '/health-house-stations'
      },
      {
        id: 'health-house-connectors',
        label: t('healthHouseConnectors.menuLabel'),
        icon: 'healthHouseConnectors',
        path: '/health-house-connectors'
      }
    );

    if (debugEnabled) {
      menuItems.push({
        id: 'debug-demo',
        label: t('debugDemo.menuLabel'),
        icon: 'debugDemo',
        path: '/admin/debug-demo'
      });
    }
  }

  // 检查当前路径是否匹配菜单项
  const isActiveMenuItem = (path) => {
    const currentPath = location.pathname;
    
    if (path === '/') {
      return currentPath === '/';
    }

    if (path === '/doctor' || path === '/admin') {
      return currentPath === path;
    }
    
    // 特殊处理报告相关路径
    if (path === '/reports') {
      // 待审核报告菜单项：包括 /reports 和 /reports/:id/review
      return currentPath === '/reports' || currentPath.match(/^\/reports\/\d+\/review$/);
    }
    
    if (path === '/reviewed-reports') {
      // 已审核报告菜单项：包括 /reviewed-reports 和 /reports/:id/view
      return currentPath.startsWith('/reviewed-reports') || currentPath.match(/^\/reports\/\d+\/view$/);
    }

    if (path === '/appointment-orders') {
      return currentPath.startsWith('/appointment-orders');
    }

    if (path === '/patients') {
      return currentPath.startsWith('/patients');
    }

    if (path === '/user-center') {
      return currentPath.startsWith('/user-center');
    }
    
    return currentPath.startsWith(path);
  };

  return (
    <aside className="app-sidebar">
      <div className="app-sidebar__brand">
        <img
          className="app-sidebar__brand-mark"
          src={logo}
          alt=""
        />
        <div className="app-sidebar__brand-copy">
          <h2 className="app-sidebar__brand-title">
            {activeWorkspace === 'doctor' ? 'Lumen AI' : (isCollapsed ? 'Lumen' : t('dashboard.title'))}
          </h2>
          {!isCollapsed && (
            <span className="app-sidebar__brand-subtitle">
              {activeWorkspace === 'admin' ? t('dashboard.adminArea') : t('dashboard.doctorDesk')}
            </span>
          )}
        </div>
      </div>

      <nav className="app-sidebar__nav">
        {menuItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`app-sidebar__link ${isActiveMenuItem(item.path) ? 'is-active' : ''}`}
            onClick={() => navigate(item.path)}
          >
            <span className="app-sidebar__icon">
              {iconComponents[item.icon]()}
            </span>
            {!isCollapsed && <span>{item.label}</span>}
          </button>
        ))}
      </nav>

      <div className="app-sidebar__footer">
        {!isCollapsed && (
          <span className="app-sidebar__brand-subtitle">
            {activeWorkspace === 'admin'
              ? t('dashboard.admin')
              : currentUser
                ? currentUser.name
                : isAdminAuthenticated
                  ? t('dashboard.admin')
                  : t('dashboard.visitor')}
          </span>
        )}
        <button
          type="button"
          className="app-sidebar__link"
          onClick={onToggleCollapse}
        >
          <span className="app-sidebar__icon">
            {iconComponents[isCollapsed ? 'expand' : 'collapse']()}
          </span>
          {!isCollapsed && <span>{t('dashboard.collapse')}</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
